import { z } from 'zod'
import { wallClockSchema } from '../validators/temporal.validator.js'
import { nullableCalendarDaySchema } from '../validators/calendar-day.validator.js'
import { eventLifecycleSchema } from '../enums/event-lifecycle.enum.js'
import { vendorServiceSchema } from '../enums/vendor-service.enum.js'
import { publicPhaseDtoSchema } from './public-schedule.dto.js'

/**
 * An event a vendor company is on, as its Events list shows it
 * (docs/planner-vendors.md).
 *
 * The list spans every venue and every organization the company works with,
 * so it names both. It shows the company's own events and never anybody
 * else's at the same venue.
 */
export const vendorEventListItemDtoSchema = z.object({
  eventId: z.string().uuid(),
  name: z.string(),
  lifecycle: eventLifecycleSchema,
  date: nullableCalendarDaySchema,
  startsAt: wallClockSchema.nullable(),
  endsAt: wallClockSchema.nullable(),
  /**
   * Whether the event is over, judged in the venue's own time zone, which is
   * what puts it under Past. Computed here rather than in the browser, which
   * may be in another time zone from the venue.
   */
  hasFinished: z.boolean(),
  venueName: z.string(),
  organizationName: z.string(),
  /**
   * The organization's account is locked for billing. The event stays listed,
   * marked, and opening it shows what the couple sees: that the venue's account
   * is unavailable. The lock is judged per event, so a planner working for two
   * organizations loses only the locked one's.
   */
  unavailable: z.boolean(),
  /**
   * Why the company is on the event, the two ways it can be (decision 25): each
   * service it is assigned here for, and whether furniture of its own is rented
   * on it. At least one holds. A planning one opens the ordinary event page;
   * any other, the vendor's own read-only view. `services` is empty for a
   * company here only through its rentals.
   */
  services: z.array(vendorServiceSchema),
  /** Defaulted while an older API is still answering. */
  rents: z.boolean().default(false),
})

export type VendorEventListItemDto = z.infer<typeof vendorEventListItemDtoSchema>

export const vendorEventListDtoSchema = z.object({
  events: z.array(vendorEventListItemDtoSchema),
  total: z.number().int().nonnegative(),
})

export type VendorEventListDto = z.infer<typeof vendorEventListDtoSchema>

/** One of the company's own pieces rented for the event. */
export const vendorEventRentalDtoSchema = z.object({
  itemName: z.string(),
  quantity: z.number().int().nonnegative(),
  /** Taken off the event, kept while a plan still has it. */
  removed: z.boolean(),
})

/** One moment in the run of the day. */
export const vendorEventMomentDtoSchema = z.object({
  label: z.string(),
  startsAt: wallClockSchema,
})

/**
 * An event as a vendor that is not its planner sees it (docs/planner-vendors.md,
 * decisions 17, 25 and 38): its name, day, guest count, rooms, run of the day
 * and plans, read-only, and for a company here through its rentals, those
 * rentals. Nothing else — no contacts, documents or notes beyond the plans'.
 *
 * **A closed list, not the event detail with fields removed.** A field added to
 * the event later cannot reach a florist unless someone adds it here, and its
 * test names every field so that addition is a decision.
 */
export const vendorEventDtoSchema = z.object({
  eventId: z.string().uuid(),
  name: z.string(),
  lifecycle: eventLifecycleSchema,
  date: nullableCalendarDaySchema,
  startsAt: wallClockSchema.nullable(),
  endsAt: wallClockSchema.nullable(),
  hasFinished: z.boolean(),
  guestCount: z.number().int().nonnegative(),
  venueName: z.string(),
  organizationName: z.string(),
  services: z.array(vendorServiceSchema),
  rentals: z.array(vendorEventRentalDtoSchema),
  timeline: z.array(vendorEventMomentDtoSchema),
  /** Each room and its plan, drawn as a crew sees it: the same closed shape. */
  phases: z.array(publicPhaseDtoSchema),
})

export type VendorEventDto = z.infer<typeof vendorEventDtoSchema>
